import { FC, ReactNode } from 'react';

import {
  BUTTON_TYPE_LINK,
  BUTTON_TYPE_ROUTE,
  CardSection,
  PrimaryButton,
} from './Base';
import Emojis, {
  TYPE_DOLLAR,
  TYPE_HP,
  TYPE_LEGAL,
  TYPE_PREMIUM,
} from './Emojis';

import { APPS, APPS_LIST, BROWSER_NAMES, CURRENT_APP } from '../constants';
import { getExtensionLanding } from '../extension/Provider';
import { AndList } from './AndList';
import './InfoCard.scss';

type EmojisType =
  typeof TYPE_HP | typeof TYPE_PREMIUM | typeof TYPE_DOLLAR | typeof TYPE_LEGAL;

type InfoCardButtonType = typeof BUTTON_TYPE_LINK | typeof BUTTON_TYPE_ROUTE;

type InfoCardProps = {
  title: string;
  text?: ReactNode;
  smallText?: ReactNode;
  emojisType?: EmojisType;
  buttonText: string;
  buttonLink: string;
  buttonType?: InfoCardButtonType;
  buttonClassName?: string;
};

const InfoCard: FC<InfoCardProps> = ({
  title,
  text,
  smallText,
  emojisType = TYPE_HP,
  buttonText,
  buttonLink,
  buttonType,
  buttonClassName,
}) => {
  const linkProps =
    buttonType === BUTTON_TYPE_ROUTE
      ? ({ type: BUTTON_TYPE_ROUTE, to: buttonLink } as const)
      : ({ type: BUTTON_TYPE_LINK, href: buttonLink } as const);
  return (
    <CardSection className="info-card">
      <div className="inner">
        <h2>{title}</h2>
        {text && <p>{text}</p>}
        <Emojis type={emojisType} />
        {smallText && <p className="dim small text-pretty">{smallText}</p>}
      </div>
      <PrimaryButton className={buttonClassName} size="large" {...linkProps}>
        {buttonText}
      </PrimaryButton>
    </CardSection>
  );
};

export default InfoCard;

//

export const FPSCFreeInfoCard: FC<{
  extensionIsInstalled: boolean;
  extensionOrigins: string[];
}> = ({ extensionIsInstalled, extensionOrigins }) => {
  let url: string;
  let text: string;
  if (extensionIsInstalled && extensionOrigins.length) {
    url = getExtensionLanding(extensionOrigins);
    text = `Installed on ${BROWSER_NAMES[CURRENT_APP.browser]}`;
  } else {
    url = CURRENT_APP.webstore_url;
    text = `Add to ${BROWSER_NAMES[CURRENT_APP.browser]} (it’s free!)`;
  }
  return (
    <InfoCard
      title="GFP Free"
      text="The best free extension to screenshot and save your full webpage."
      emojisType={TYPE_HP}
      smallText={
        <>
          The highest rated screenshot extension in the{' '}
          <a href={APPS.chrome.webstore_url}>Chrome web store</a>. Also
          available on{' '}
          <AndList
            items={APPS_LIST.filter((app) => app.browser !== 'chrome').map(
              (app) => ({
                url: app.webstore_url,
                text: BROWSER_NAMES[app.browser],
              }),
            )}
          />
          .
        </>
      }
      buttonText={text}
      buttonLink={url}
      buttonType={BUTTON_TYPE_LINK}
      buttonClassName={'btn-standout'}
    />
  );
};

type PremiumInfoCardProps = {
  buttonText?: string;
};

export const PremiumInfoCard: FC<PremiumInfoCardProps> = ({ buttonText }) => (
  <InfoCard
    title="Go Premium"
    text="Take your screenshots to the next level with GoFullPage Premium."
    emojisType={TYPE_PREMIUM}
    smallText="Including advanced editing and annotation features of screenshots."
    buttonText={buttonText || 'Get started (free 1-week trial)'}
    buttonLink={'/premium'}
    buttonType={BUTTON_TYPE_ROUTE}
    buttonClassName={'btn-convert btn-standout'}
  />
);
